<script setup>
import { ref, computed, onBeforeUnmount } from 'vue';
import { OnClickOutside } from '@vueuse/components';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';

const props = defineProps({
  type: {
    type: String,
    default: 'edit',
    validator: value => ['alert', 'edit'].includes(value),
  },
  title: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
  cancelButtonLabel: {
    type: String,
    default: '',
  },
  confirmButtonLabel: {
    type: String,
    default: '',
  },
  disableConfirmButton: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  showCancelButton: {
    type: Boolean,
    default: true,
  },
  showConfirmButton: {
    type: Boolean,
    default: true,
  },
  overflowYAuto: {
    type: Boolean,
    default: false,
  },
  width: {
    type: String,
    default: 'lg',
    validator: value =>
      ['5xl', '4xl', '3xl', '2xl', 'xl', 'lg', 'md', 'sm'].includes(value),
  },
  position: {
    type: String,
    default: 'center',
    validator: value => ['center', 'top'].includes(value),
  },
  resizable: {
    type: Boolean,
    default: false,
  },
  fullscreen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['confirm', 'close']);

const { t } = useI18n();

const dialogRef = ref(null);
const dialogContentRef = ref(null);
const isOpen = ref(false);
const isFullscreen = ref(false);

const toggleFullscreen = () => {
  isFullscreen.value = !isFullscreen.value;
};

// ─── Vertical drag-to-resize ──────────────────────────────
const isDragging = ref(false);
let dragStartY = 0;
let dragStartHeight = 0;

const handleDragStart = e => {
  if (isFullscreen.value || !dialogRef.value) return;
  isDragging.value = true;
  dragStartY = e.clientY;
  dragStartHeight = dialogRef.value.offsetHeight;
  document.body.style.cursor = 'row-resize';
  document.body.style.userSelect = 'none';
  document.addEventListener('mousemove', handleDragMove);
  document.addEventListener('mouseup', handleDragEnd);
  e.preventDefault();
};

const handleDragMove = e => {
  if (!isDragging.value) return;
  const deltaY = e.clientY - dragStartY;
  const newHeight = dragStartHeight + deltaY;
  const minHeight = 320; // 20rem
  const maxHeight = window.innerHeight * 0.95;
  const clamped = Math.max(minHeight, Math.min(maxHeight, newHeight));
  dialogRef.value.style.height = `${clamped}px`;
  dialogRef.value.style.maxHeight = `${clamped}px`;
};

const handleDragEnd = () => {
  isDragging.value = false;
  document.body.style.cursor = '';
  document.body.style.userSelect = '';
  document.removeEventListener('mousemove', handleDragMove);
  document.removeEventListener('mouseup', handleDragEnd);
};

onBeforeUnmount(() => {
  document.removeEventListener('mousemove', handleDragMove);
  document.removeEventListener('mouseup', handleDragEnd);
});
// ─── /Vertical drag-to-resize ─────────────────────────────

const maxWidthClass = computed(() => {
  if (isFullscreen.value) return '';
  const classesMap = {
    '5xl': 'max-w-5xl',
    '4xl': 'max-w-4xl',
    '3xl': 'max-w-3xl',
    '2xl': 'max-w-2xl',
    xl: 'max-w-xl',
    lg: 'max-w-lg',
    md: 'max-w-md',
    sm: 'max-w-sm',
  };

  return classesMap[props.width] ?? 'max-w-md';
});

const positionClass = computed(() =>
  props.position === 'top' ? 'dialog-position-top' : ''
);

const open = () => {
  isFullscreen.value = props.fullscreen;
  // Reset any inline height from previous drag
  if (dialogRef.value) {
    dialogRef.value.style.height = '';
    dialogRef.value.style.maxHeight = '';
  }
  isOpen.value = true;
  dialogRef.value?.showModal();
};

const close = () => {
  emit('close');
  dialogRef.value?.close();
  isOpen.value = false;
};

// Only close if the close event originated from this dialog,
// not from a child dialog (e.g. ProseMirror prompt) bubbling up.
const handleDialogClose = e => e.target === dialogRef.value && close();

// Only close on click-outside if this dialog is the topmost one.
// If another dialog (e.g. ProseMirror prompt) is open on top, ignore.
const handleClickOutside = () => {
  const dialogs = document.querySelectorAll('dialog[open]');
  if (dialogs[dialogs.length - 1] === dialogRef.value) close();
};

const confirm = () => {
  emit('confirm');
};

defineExpose({ open, close });
</script>

<template>
  <TeleportWithDirection to="body">
    <dialog
      ref="dialogRef"
      class="w-full flex flex-col transition-all duration-300 ease-in-out shadow-xl rounded-xl"
      :style="resizable || isFullscreen ? { overflow: 'hidden' } : {}"
      :class="[
        isFullscreen ? 'dialog-fullscreen' : maxWidthClass,
        isFullscreen ? '' : positionClass,
        resizable ? 'overflow-hidden' : (overflowYAuto ? 'overflow-y-auto' : 'overflow-visible'),
        resizable && !isFullscreen ? 'dialog-resizable' : '',
      ]"
      @close.prevent="handleDialogClose"
    >
      <OnClickOutside @trigger="handleClickOutside">
        <div class="flex flex-col h-full min-h-0 w-full">
        <form
          ref="dialogContentRef"
          class="flex flex-col w-full gap-6 p-6 overflow-hidden text-start align-middle transition-all duration-300 ease-in-out transform bg-n-alpha-3 backdrop-blur-[100px] shadow-xl rounded-xl"
          :class="resizable || isFullscreen ? 'h-full min-h-0 flex-1' : 'h-auto'"
          @submit.prevent="confirm"
          @click.stop
        >
          <div
            v-if="resizable && (title || description)"
            class="flex items-start justify-between gap-2"
          >
            <div class="flex flex-col gap-2 flex-1 min-w-0">
              <h3 class="text-base font-medium leading-6 text-n-slate-12">
                {{ title }}
              </h3>
              <slot name="description">
                <p v-if="description" class="mb-0 text-sm text-n-slate-11">
                  {{ description }}
                </p>
              </slot>
            </div>
            <Button
              v-if="resizable"
              color="slate"
              size="sm"
              class="shrink-0 w-8 h-8 p-0"
              :title="
                isFullscreen
                  ? t('DIALOG.BUTTONS.EXIT_FULLSCREEN')
                  : t('DIALOG.BUTTONS.FULLSCREEN')
              "
              @click="toggleFullscreen"
            >
              <Icon
                :icon="
                  isFullscreen
                    ? 'i-lucide-minimize-2'
                    : 'i-lucide-maximize-2'
                "
                class="size-4"
              />
            </Button>
          </div>
          <div
            v-if="!resizable && (title || description)"
            class="flex flex-col gap-2"
          >
            <h3 class="text-base font-medium leading-6 text-n-slate-12">
              {{ title }}
            </h3>
            <slot name="description">
              <p v-if="description" class="mb-0 text-sm text-n-slate-11">
                {{ description }}
              </p>
            </slot>
          </div>
          <slot v-if="isOpen && !resizable" />
          <div
            v-if="isOpen && resizable"
            class="flex-1 min-h-0 flex flex-col overflow-y-auto"
          >
            <slot />
          </div>
          <slot name="footer">
            <div
              v-if="showCancelButton || showConfirmButton"
              class="flex items-center justify-between w-full gap-3"
            >
              <Button
                v-if="showCancelButton"
                variant="faded"
                color="slate"
                :label="cancelButtonLabel || t('DIALOG.BUTTONS.CANCEL')"
                class="w-full"
                type="button"
                @click="close"
              />
              <Button
                v-if="showConfirmButton"
                :color="type === 'edit' ? 'blue' : 'ruby'"
                :label="confirmButtonLabel || t('DIALOG.BUTTONS.CONFIRM')"
                class="w-full"
                :is-loading="isLoading"
                :disabled="disableConfirmButton || isLoading"
                type="submit"
              />
            </div>
          </slot>
        </form>
        </div>
      </OnClickOutside>
      <!-- Visible vertical resize grip (bottom edge) -->
      <div
        v-if="resizable && !isFullscreen"
        class="dialog-resize-grip"
        @mousedown="handleDragStart"
      >
        <span class="grip-handle" />
      </div>
    </dialog>
  </TeleportWithDirection>
</template>

<style scoped>
/* dialog::backdrop needs to be unscoped */
dialog::backdrop {
  @apply bg-n-alpha-black1 backdrop-blur-[4px];
}

/* CRITICAL: Tailwind's flex utility overrides the native <dialog> default
 * of display:none when [open] is absent. Without this, ALL dialog elements
 * are visible simultaneously — even ones that never had showModal() called. */
dialog:not([open]) {
  display: none;
}

.dialog-position-top {
  margin-top: clamp(2rem, 5vh, 5rem);
  margin-bottom: auto;
}

/* 80% viewport width/height for large dialogs */
.dialog-w-80 {
  width: 80vw;
  max-width: 80vw;
  height: 80vh;
  max-height: 80vh;
}

/* Resizable dialog — custom grip bar replaces tiny native handle */
.dialog-resizable {
  overflow: hidden;
  min-width: 28rem;
  min-height: 20rem;
}

/* CRITICAL: Force OnClickOutside root div to fill dialog in flex-col */
.dialog-resizable > :deep(*),
.dialog-fullscreen > :deep(*) {
  flex: 1 1 0%;
  min-height: 0;
  overflow: hidden;
}

/* Fullscreen mode */
.dialog-fullscreen {
  width: 100vw;
  height: 100vh;
  max-width: 100vw;
  max-height: 100vh;
  border-radius: 0;
}

/* ─── Vertical resize grip bar ───────────────────────── */
.dialog-resize-grip {
  flex: none !important;
  max-height: none !important;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 14px;
  cursor: row-resize;
  border-top: 1px solid var(--w-100, rgba(0, 0, 0, 0.06));
  background: rgba(0, 0, 0, 0.02);
  border-radius: 0 0 12px 12px;
  transition: background 0.15s ease;
  user-select: none;
}

.dialog-resize-grip:hover {
  background: rgba(0, 0, 0, 0.05);
}

.dialog-resize-grip:active {
  background: rgba(59, 130, 246, 0.1);
}

.grip-handle {
  width: 40px;
  height: 4px;
  border-radius: 2px;
  background: #94a3b8;
  opacity: 0.4;
  transition: opacity 0.15s ease, background 0.15s ease;
}

.dialog-resize-grip:hover .grip-handle {
  opacity: 0.9;
  background: #64748b;
}
</style>
